export const DEFAULT_REVIEW_SETTINGS = {
    toleranceMinor: 100,
    tolerancePct: 0.005,
    partialPaymentGraceDays: 3,
    depositGraceDays: 60,
    longPaymentDays: 180,
    duplicateWindowMinutes: 10,
};
export function toleranceFor(totalMinor, settings) {
    return Math.max(settings.toleranceMinor, Math.round(Math.abs(totalMinor) * settings.tolerancePct));
}
export function summarizeMoney(ctx, settings) {
    const completed = ctx.payments.filter((p) => p.status === 'completed');
    const paidMinor = completed.reduce((sum, p) => sum + p.amountMinor, 0);
    const netPaidMinor = paidMinor - ctx.refundedMinor;
    const firstPaidAt = completed.reduce((first, p) => (first === null || p.receivedAt < first ? p.receivedAt : first), null);
    const toleranceMinor = toleranceFor(ctx.totalMinor, settings);
    // Whether the buyer paid what was owed is a question about money received,
    // before refunds: a bed paid in full and later refunded $200 was not "part
    // paid", and neither site lowers the order total when it refunds. Overpaid
    // is about money kept, so an overpayment already refunded is not flagged.
    let paymentState;
    if (paidMinor <= 0)
        paymentState = 'none';
    else if (paidMinor < ctx.totalMinor - toleranceMinor)
        paymentState = 'partial';
    else if (netPaidMinor > ctx.totalMinor + toleranceMinor)
        paymentState = 'over';
    else
        paymentState = 'full';
    const expectedTotalMinor = ctx.itemsGrossMinor - ctx.discountMinor + ctx.shippingMinor + (ctx.taxRemittedByChannel ? 0 : ctx.taxMinor);
    return { paidMinor, refundedMinor: ctx.refundedMinor, netPaidMinor, firstPaidAt, paymentState, expectedTotalMinor, toleranceMinor };
}
const usd = (minor) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(minor / 100);
const day = (date) => date.toISOString().slice(0, 10);
const DAY_MS = 86_400_000;
const ageDays = (ctx) => (ctx.now.getTime() - ctx.placedAt.getTime()) / DAY_MS;
const baseEvidence = (ctx, money) => ({
    status: ctx.channelStatus,
    state: ctx.state,
    totalMinor: ctx.totalMinor,
    paidMinor: money.paidMinor,
    refundedMinor: money.refundedMinor,
    netPaidMinor: money.netPaidMinor,
    placedDate: day(ctx.placedAt),
});
export const REVIEW_RULES = [
    {
        id: 'cancelled_with_payment',
        label: 'Cancelled but payment kept',
        severity: 'conflict',
        description: 'The order is cancelled, yet payments received exceed refunds.',
        evaluate(ctx, money) {
            if (ctx.state !== 'cancelled' || money.netPaidMinor <= money.toleranceMinor)
                return null;
            return {
                message: `Cancelled, but ${usd(money.paidMinor)} was received and only ${usd(money.refundedMinor)} refunded`,
                evidence: baseEvidence(ctx, money),
                amountAtRiskMinor: money.netPaidMinor,
                defaultTreatment: 'exclude',
            };
        },
    },
    {
        id: 'completed_partial_payment',
        label: 'Completed with partial payment',
        severity: 'conflict',
        description: 'The order is completed or shipped, but less than the order total was received.',
        evaluate(ctx, money) {
            if (ctx.basis !== 'payments' || ctx.state !== 'fulfilled')
                return null;
            if (money.paymentState !== 'partial')
                return null;
            return {
                message: `Completed, but only ${usd(money.paidMinor)} of ${usd(ctx.totalMinor)} was received`,
                evidence: baseEvidence(ctx, money),
                amountAtRiskMinor: ctx.totalMinor - money.paidMinor,
                defaultTreatment: 'count_payments',
            };
        },
    },
    {
        id: 'completed_no_payment',
        label: 'Completed with no payment recorded',
        severity: 'conflict',
        description: 'The order is completed or shipped, but no payment was recorded — often a phone order entered without its payment.',
        evaluate(ctx, money) {
            if (ctx.basis !== 'payments' || ctx.state !== 'fulfilled')
                return null;
            if (money.paidMinor > 0 || ctx.totalMinor <= money.toleranceMinor)
                return null;
            return {
                message: `Completed with no payment recorded against a ${usd(ctx.totalMinor)} order`,
                evidence: baseEvidence(ctx, money),
                amountAtRiskMinor: ctx.totalMinor,
                defaultTreatment: 'exclude',
            };
        },
    },
    {
        id: 'partial_payment',
        label: 'Open order partly paid',
        severity: 'warning',
        description: 'An open order has received some, but not all, of its total, past the grace period.',
        evaluate(ctx, money, settings) {
            if (ctx.basis !== 'payments' || (ctx.state !== 'open' && ctx.state !== 'pending'))
                return null;
            if (money.paymentState !== 'partial')
                return null;
            const grace = ctx.depositsExpected ? settings.depositGraceDays : settings.partialPaymentGraceDays;
            if (ageDays(ctx) <= grace)
                return null;
            return {
                message: `${usd(money.paidMinor)} of ${usd(ctx.totalMinor)} received, still open after ${Math.floor(ageDays(ctx))} days`,
                evidence: { ...baseEvidence(ctx, money), graceDays: grace },
                amountAtRiskMinor: ctx.totalMinor - money.paidMinor,
                defaultTreatment: 'count_payments',
            };
        },
    },
    {
        id: 'unpaid_open_order',
        label: 'Open order with no payment',
        severity: 'warning',
        description: 'An open order has no payment recorded past the grace period, so it counts as nothing yet.',
        evaluate(ctx, money, settings) {
            if (ctx.basis !== 'payments' || ctx.state !== 'open')
                return null;
            if (money.paidMinor > 0 || ctx.totalMinor <= money.toleranceMinor)
                return null;
            const grace = ctx.depositsExpected ? settings.depositGraceDays : settings.partialPaymentGraceDays;
            if (ageDays(ctx) <= grace)
                return null;
            return {
                message: `Open for ${Math.floor(ageDays(ctx))} days with no payment recorded against ${usd(ctx.totalMinor)}`,
                evidence: { ...baseEvidence(ctx, money), graceDays: grace },
                amountAtRiskMinor: ctx.totalMinor,
                defaultTreatment: 'exclude',
            };
        },
    },
    {
        id: 'overpaid',
        label: 'Paid more than the order total',
        severity: 'conflict',
        description: 'Payments received, less refunds, exceed the order total.',
        evaluate(ctx, money) {
            if (money.paymentState !== 'over')
                return null;
            return {
                message: `${usd(money.netPaidMinor)} received against a ${usd(ctx.totalMinor)} order`,
                evidence: baseEvidence(ctx, money),
                amountAtRiskMinor: money.netPaidMinor - ctx.totalMinor,
                defaultTreatment: 'count_payments',
            };
        },
    },
    {
        id: 'payment_on_incomplete_order',
        label: 'Payment on an unfinished checkout',
        severity: 'warning',
        description: 'The order is still a cart, checkout or abandoned order, yet a payment was received.',
        evaluate(ctx, money) {
            if (ctx.state !== 'pending' || money.netPaidMinor <= money.toleranceMinor)
                return null;
            return {
                message: `Status "${ctx.channelStatus}", but ${usd(money.netPaidMinor)} was received`,
                evidence: baseEvidence(ctx, money),
                amountAtRiskMinor: money.netPaidMinor,
                defaultTreatment: 'count_payments',
            };
        },
    },
    {
        id: 'refund_exceeds_payment',
        label: 'Refunded more than received',
        severity: 'conflict',
        description: 'Refunds exceed what was paid (or, on a marketplace, the order total).',
        evaluate(ctx, money) {
            const ceiling = ctx.basis === 'payments' ? money.paidMinor : ctx.totalMinor;
            if (money.refundedMinor <= ceiling + money.toleranceMinor)
                return null;
            return {
                message: `${usd(money.refundedMinor)} refunded against ${usd(ceiling)} ${ctx.basis === 'payments' ? 'received' : 'order total'}`,
                evidence: baseEvidence(ctx, money),
                amountAtRiskMinor: money.refundedMinor - ceiling,
                defaultTreatment: 'exclude',
            };
        },
    },
    {
        id: 'duplicate_payment',
        label: 'Possible duplicate payment',
        severity: 'warning',
        description: 'Two payments of the same amount arrived within a few minutes of each other.',
        evaluate(ctx, money, settings) {
            const completed = ctx.payments
                .filter((p) => p.status === 'completed' && p.amountMinor > 0)
                .sort((a, b) => a.receivedAt.getTime() - b.receivedAt.getTime());
            const windowMs = settings.duplicateWindowMinutes * 60_000;
            for (let i = 1; i < completed.length; i++) {
                const previous = completed[i - 1];
                const current = completed[i];
                if (!previous || !current)
                    continue;
                if (previous.amountMinor === current.amountMinor &&
                    current.receivedAt.getTime() - previous.receivedAt.getTime() <= windowMs) {
                    return {
                        message: `Two payments of ${usd(current.amountMinor)} within ${settings.duplicateWindowMinutes} minutes`,
                        evidence: { ...baseEvidence(ctx, money), duplicateAmountMinor: current.amountMinor },
                        amountAtRiskMinor: current.amountMinor,
                        defaultTreatment: null,
                    };
                }
            }
            return null;
        },
    },
    {
        id: 'totals_mismatch',
        label: "Order total doesn't match its parts",
        severity: 'warning',
        description: 'Merchandise − discounts + shipping + tax does not equal the order total.',
        evaluate(ctx, money) {
            if (ctx.itemCount === 0)
                return null;
            const difference = ctx.totalMinor - money.expectedTotalMinor;
            if (Math.abs(difference) <= money.toleranceMinor)
                return null;
            return {
                message: `Parts add up to ${usd(money.expectedTotalMinor)}, but the order total is ${usd(ctx.totalMinor)}`,
                evidence: { ...baseEvidence(ctx, money), expectedTotalMinor: money.expectedTotalMinor },
                amountAtRiskMinor: Math.abs(difference),
                defaultTreatment: null,
            };
        },
    },
    {
        id: 'zero_total_with_items',
        label: 'Zero total with products',
        severity: 'warning',
        description: 'The order carries priced products but its total is zero or negative.',
        evaluate(ctx, money) {
            if (ctx.state === 'cancelled' || ctx.itemCount === 0)
                return null;
            if (ctx.totalMinor > 0 || ctx.itemsGrossMinor <= 0)
                return null;
            return {
                message: `${usd(ctx.itemsGrossMinor)} of products on an order totalling ${usd(ctx.totalMinor)}`,
                evidence: baseEvidence(ctx, money),
                amountAtRiskMinor: ctx.itemsGrossMinor,
                defaultTreatment: null,
            };
        },
    },
    {
        id: 'no_line_items',
        label: 'Payment with no products',
        severity: 'warning',
        description: 'Money was received, but the order has no product lines to attribute it to.',
        evaluate(ctx, money) {
            if (ctx.itemCount > 0 || money.netPaidMinor <= money.toleranceMinor)
                return null;
            return {
                message: `${usd(money.netPaidMinor)} received on an order with no products`,
                evidence: baseEvidence(ctx, money),
                amountAtRiskMinor: money.netPaidMinor,
                defaultTreatment: null,
            };
        },
    },
    {
        id: 'payment_long_after_order',
        label: 'Paid long after ordering',
        severity: 'info',
        description: 'The first payment arrived months after the order was placed.',
        evaluate(ctx, money, settings) {
            if (!money.firstPaidAt)
                return null;
            const days = (money.firstPaidAt.getTime() - ctx.placedAt.getTime()) / DAY_MS;
            if (days <= settings.longPaymentDays)
                return null;
            return {
                message: `First payment on ${day(money.firstPaidAt)}, ${Math.floor(days)} days after the order`,
                evidence: { ...baseEvidence(ctx, money), firstPaidDate: day(money.firstPaidAt) },
                amountAtRiskMinor: 0,
                defaultTreatment: null,
            };
        },
    },
    {
        id: 'unlinked_product',
        label: 'Product not linked',
        severity: 'info',
        description: 'A sold line matches no product in the catalog, so SKU reports miss it.',
        evaluate(ctx, money) {
            if (ctx.unlinkedLineCount === 0 || ctx.state === 'cancelled')
                return null;
            return {
                message: `${ctx.unlinkedLineCount} line(s) match no product`,
                evidence: { ...baseEvidence(ctx, money), unlinkedLines: ctx.unlinkedLineCount },
                amountAtRiskMinor: 0,
                defaultTreatment: null,
            };
        },
    },
    {
        id: 'unattributed_site',
        label: 'Site unknown',
        severity: 'info',
        description: 'A marketplace line could not be placed in MLF or MFF, so it counts in Combined only.',
        evaluate(ctx, money) {
            if (ctx.unattributedLineCount === 0 || ctx.state === 'cancelled')
                return null;
            return {
                message: `${ctx.unattributedLineCount} line(s) could not be attributed to MLF or MFF`,
                evidence: { ...baseEvidence(ctx, money), unattributedLines: ctx.unattributedLineCount },
                amountAtRiskMinor: 0,
                defaultTreatment: null,
            };
        },
    },
];
export const REVIEW_RULE_IDS = REVIEW_RULES.map((rule) => rule.id);
export function reviewOrder(ctx, settings = DEFAULT_REVIEW_SETTINGS) {
    const money = summarizeMoney(ctx, settings);
    const findings = [];
    for (const rule of REVIEW_RULES) {
        const result = rule.evaluate(ctx, money, settings);
        if (result)
            findings.push({ ruleId: rule.id, severity: rule.severity, ...result });
    }
    return { money, findings };
}
//# sourceMappingURL=sales-review-rules.js.map